import { Stack } from "@mui/material";
import { useNavigate, NavigateFunction } from "react-router-dom";
import { StartingCard } from "./StartingCard";
import { StartingCardRemix } from "./StartingCardRemix";
import { useRemixMode } from "../featureFlags/useRemixMode";

export type StartingCardId = "concept" | "redesign" | "board";

interface StartingCardData {
  id: StartingCardId;
  file: string;
  // Remix-mode artwork (title + arrow baked in), shown only to remix users.
  remixFile: string;
  title: string;
  subtitle: string;
  go: (navigate: NavigateFunction) => void;
}

// The three cards map to HomeSideBar's create menu (HomeSideBar.tsx:443-484).
// Order (left → right): Redesign a Room, Create New Board, Generate New Concept.
const CARDS: StartingCardData[] = [
  {
    id: "redesign",
    file: "starting-option-2.jpg",
    remixFile: "starting-option-remix-2-v2.jpg",
    title: "Redesign a Room",
    subtitle: "Upload a photo to change materials and furniture",
    go: (navigate) => navigate("/home/concepts/new?mode=image"),
  },
  {
    id: "board",
    file: "starting-option-3.jpg",
    remixFile: "starting-option-remix-3.jpg",
    title: "Create New Board",
    subtitle: "Build 3D material boards with real products",
    // First visit → a starter template board (loads objects + lighting); after that → a
    // plain new board. Full reload to leave the SPA's /home tree (matches HomeSideBar.tsx:476).
    go: () => {
      let seen = false;
      try {
        seen = !!localStorage.getItem("hasSeenTemplateBoard");
        localStorage.setItem("hasSeenTemplateBoard", "1");
      } catch {
        /* private mode / blocked storage: fall through to the template board */
      }
      window.location.assign(
        seen ? "/board/new" : "/board/new_from?id=1jqh81ou4b",
      );
    },
  },
  {
    id: "concept",
    file: "starting-option-1.jpg",
    remixFile: "starting-option-remix-1.jpg",
    title: "Generate New Concept",
    subtitle: "Editorial-style images with matching materials",
    go: (navigate) => navigate("/home/concepts/new"),
  },
];

/**
 * The three quick-create cards (Redesign a Room / Create New Board /
 * Generate New Concept) on /starting-options.
 */
export const StartingCardsRow = ({
  maxWidth = 1900,
  onCardClick,
}: {
  maxWidth?: number;
  /** Fired before navigating (analytics on /starting-options). */
  onCardClick?: (card: StartingCardId) => void;
}) => {
  const navigate = useNavigate();
  const remixMode = useRemixMode();
  const select = (card: StartingCardData) => {
    onCardClick?.(card.id);
    card.go(navigate);
  };

  return (
    <Stack
      direction={{ xs: "column", md: "row" }}
      spacing={{ xs: 2, md: 3 }}
      alignItems="stretch"
      sx={{ maxWidth, width: "100%", mx: "auto" }}
    >
      {CARDS.map((card) =>
        remixMode ? (
          <StartingCardRemix
            key={card.file}
            img={`/images/${card.remixFile}`}
            title={card.title}
            onClick={() => select(card)}
          />
        ) : (
          <StartingCard
            key={card.file}
            img={`/images/${card.file}`}
            title={card.title}
            subtitle={card.subtitle}
            onClick={() => select(card)}
          />
        ),
      )}
    </Stack>
  );
};
